import { ListResponseType } from "src/types";
import CoreAPI from "./CoreAPI";
import { transformAPIKeyToCamel } from "./utils/transformAPIKeyToCamel";
import { transformCamelToSnake } from "./utils/transformCamelToSnake";

const apiClient = new CoreAPI();

// ===== Types =====

export interface FetchSCMVendorListParams {
  status?: number;
  limit: number;
  offset: number;
  name?: string;
}

export interface SCMContract {
  id: number;
  vendorId: number;
  ubn: string; // 統一編號
  name: string; // 廠商名稱
  contractMode: number; // 合作類型
  contractName: string | null; // 合作類型名稱
  startDate: string | null; // 生效期間-起
  endDate: string | null; // 生效期間-訖
  status: number; // 狀態
  staffName: string | null; // 負責人
  createdAt: string; // 建立時間
  updatedAt: string; // 更新時間
  lastUpdatedBy: string | null; // 最後更新者
  categoryRates: CategoryRateDetail[];
  depositAmount: number; // 保證金
  minOrderQty: number; // 最低訂購量
  acceptableDays: number; // 允收天數
  returnableDays: number; // 允退天數
  arrivalDays: number; // 到貨天數
}

export interface SCMContractListParams {
  limit: number;
  offset: number;
  ubn?: string; // 統一編號
  name?: string; // 廠商名稱
  contractMode?: number; // 合作類型
  status?: number; // 狀態
  startDateBefore?: string; // 合約生效日起
  startDateAfter?: string; // 合約生效日訖
}

export type SCMContractListResponse = ListResponseType<SCMContract>;

export interface CategoryRateItem {
  id?: number | null;
  categoryStId: number;
  categoryNdId?: number;
  commissionRate: number;
}

export interface CategoryRateDetail extends CategoryRateItem {
  categoryStName: string;
  categoryNdName?: string;
  createdAt: string;
  updatedAt: string;
}

export interface VendorDetails {
  id: number;
  vendorId: number;
  ubn: string;
  name: string;
  phone: string;
  email: string;
  registerAddress: string;
  businessAddress: string;
  brands: string;
  currency: number;
  status: number;
  owner: number | null;
  submittedAt: string | null;
  activeAt: string | null;
  inactiveAt: string | null;
  createdAt: string;
  bankAccount: {
    accountName: string;
    accountNumber: string;
    bankName: string;
    branchName: string;
    swiftCode: string;
    copyFile: string | null;
  };
}

export interface CreateContractPayload {
  vendorId: number;
  contractMode: number;
  startDate: string;
  endDate: string;
  depositAmount?: number;
  minOrderQty?: number;
  acceptableDays?: number;
  returnableDays?: number;
  arrivalDays?: number;
  categoryRates?: CategoryRateItem[];
  force?: boolean;
}

export interface UpdateContractPayload {
  contractMode: number;
  startDate: string;
  endDate: string;
  depositAmount?: number;
  minOrderQty?: number;
  acceptableDays?: number;
  returnableDays?: number;
  arrivalDays?: number;
  categoryRates?: CategoryRateItem[];
}

interface SCMContractAPI {
  fetchSCMVendorList: (params: FetchSCMVendorListParams) => Promise<ListResponseType<VendorDetails>>;
  fetchContractList: (params: SCMContractListParams) => Promise<SCMContractListResponse>;
  fetchContractDetail: (id: number) => Promise<SCMContract>;
  createSCMContract: (payload: CreateContractPayload) => Promise<SCMContract>;
  updateSCMContract: (id: number, payload: UpdateContractPayload) => Promise<SCMContract>;
  disableSCMContract: (id: number) => Promise<void>;
}

// ===== API Functions =====
const SCMContractApi: SCMContractAPI = {
  // 取得廠商列表
  fetchSCMVendorList: async (params) => {
    const getParams = transformCamelToSnake(params);
    const response = await apiClient.getData("/manage/scm/vendor/", getParams);
    return transformAPIKeyToCamel(response.data.result);
  },
  // 取得合約列表
  fetchContractList: async (params: SCMContractListParams) => {
    const getParams = transformCamelToSnake(params);
    const response = await apiClient.getData("/manage/scm/contract/", getParams);
    return transformAPIKeyToCamel(response.data.result);
  },
  // 取得合約詳情
  fetchContractDetail: async (id: number) => {
    const response = await apiClient.getData(`/manage/scm/contract/${id}/`, {});
    return transformAPIKeyToCamel(response.data.result);
  },
  // 創建合約
  createSCMContract: async (payload: CreateContractPayload) => {
    const payloadData = transformCamelToSnake(payload);
    const response = await apiClient.postData("/manage/scm/contract/", payloadData);
    return transformAPIKeyToCamel(response.data.result);
  },
  // 更新合約
  updateSCMContract: async (id: number, payload: UpdateContractPayload) => {
    const payloadData = transformCamelToSnake(payload);
    const response = await apiClient.putData(`/manage/scm/contract/${id}/`, payloadData);
    return transformAPIKeyToCamel(response.data.result);
  },
  // 停用合約
  disableSCMContract: async (id: number) => {
    await apiClient.deleteData(`/manage/scm/contract/${id}/`, {});
  },
};

export default SCMContractApi;
